import React, { useEffect, useState } from "react";
import { Card, Spinner, Table, Row, Col } from "react-bootstrap";
import useApi from "../../hooks/useApi";
import "./AgentCommissions.css";

const AgentCommissions = () => {
  const { bearerAuth } = useApi();
  const [loading, setLoading] = useState(true);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({ 
    students_total: 0, 
    estimated_commission_total: 0, 
    city_breakdown: [] 
  });

  const loadData = async () => {
    try {
      setLoading(true);
      const res = await bearerAuth("agent/commissions");
      if (res?.data?.status) {
        setRows(res.data.data || []);
        setSummary(res.data.summary || { 
          students_total: 0, 
          estimated_commission_total: 0, 
          city_breakdown: [] 
        });
      } else {
        setRows([]);
      }
    } catch (err) {
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  // Mobile card view for each commission row
  const MobileCommissionCard = ({ row }) => (
    <div className="agent-commission-mobile-card">
      <div className="commission-header">
        <div>
          <div className="course-title">{row.course_title}</div>
          <div className="city-name">{row.city_name || "All Cities"}</div>
        </div>
        <div className="commission-badge">
          {row.commission_type === "percent" 
            ? `${Number(row.commission_value || 0).toFixed(2)}%` 
            : `₹${Number(row.commission_value || 0).toFixed(2)}`}
        </div>
      </div>

      <div className="info-row">
        <span className="info-label">Course Price</span>
        <span className="info-value">₹{Number(row.course_price || 0).toFixed(2)}</span>
      </div>

      <div className="info-row">
        <span className="info-label">Per Student</span>
        <span className="info-value">₹{Number(row.per_student_commission || 0).toFixed(2)}</span>
      </div>

      <div className="info-row">
        <span className="info-label">Students</span>
        <span className="info-value">{row.students_count || 0}</span>
      </div>

      <div className="total-row d-flex justify-content-between">
        <span>Total Commission</span>
        <span className="fw-bold">₹{Number(row.estimated_commission_total || 0).toFixed(2)}</span>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="agent-commissions-loading">
        <Spinner animation="border" variant="primary" />
        <div className="mt-2 text-muted">Loading your commission details...</div>
      </div>
    );
  }

  return (
    <div className="agent-commissions-container">
      {/* Summary Cards with Gradients */}
      <Row className="g-3 mb-4 agent-summary-cards">
        <Col xs={12} md={6}>
          <Card className="agent-summary-card">
            <Card.Body>
              <div className="text-muted">Total Registered Students</div>
              <h3 className="mb-0">{summary.students_total || 0}</h3>
            </Card.Body>
          </Card>
        </Col>
        <Col xs={12} md={6}>
          <Card className="agent-summary-card">
            <Card.Body>
              <div className="text-muted">Estimated Commission</div>
              <h3 className="mb-0">₹{Number(summary.estimated_commission_total || 0).toFixed(2)}</h3>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* City-wise Breakdown - Enhanced Cards */}
      {summary.city_breakdown?.length > 0 && (
        <Card className="shadow-sm mb-4">
          <Card.Body>
            <h6 className="mb-3">City-wise Payout Breakdown</h6>
            <Row className="g-3 agent-city-breakdown">
              {summary.city_breakdown.map((c, index) => (
                <Col xs={12} sm={6} md={4} key={c.city_id || c.city_name}>
                  <div className="agent-city-card">
                    <div className="city-name">{c.city_name}</div>
                    <div className="city-amount">₹{Number(c.estimated_commission_total || 0).toFixed(2)}</div>
                    <div className="city-students">{Number(c.students_count || 0)} students enrolled</div>
                  </div>
                </Col>
              ))}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* Commissions Table/Cards */}
      <Card className="shadow-sm">
        <Card.Body className="p-0">
          {/* Desktop Table View */}
          <div className="d-none d-md-block">
            <Table responsive hover className="mb-0 align-middle">
              <thead className="table-light">
                <tr>
                  <th>Course</th>
                  <th>City</th>
                  <th className="d-none d-lg-table-cell">Course Price</th>
                  <th className="d-none d-lg-table-cell">Commission Rule</th>
                  <th>Per Student</th>
                  <th>Students</th>
                  <th>Total Commission</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r, idx) => (
                  <tr key={`${r.course_id}-${r.city_id || "na"}-${idx}`}>
                    <td className="fw-semibold">{r.course_title}</td>
                    <td>{r.city_name || "All Cities"}</td>
                    <td className="d-none d-lg-table-cell">₹{Number(r.course_price || 0).toFixed(2)}</td>
                    <td className="d-none d-lg-table-cell">
                      {r.commission_type === "percent"
                        ? `${Number(r.commission_value || 0).toFixed(2)}%`
                        : `₹${Number(r.commission_value || 0).toFixed(2)}`}
                    </td>
                    <td className="text-primary">₹{Number(r.per_student_commission || 0).toFixed(2)}</td>
                    <td>{r.students_count || 0}</td>
                    <td className="fw-bold">₹{Number(r.estimated_commission_total || 0).toFixed(2)}</td>
                  </tr>
                ))}
                {!rows.length && (
                  <tr>
                    <td colSpan={7} className="text-center text-muted py-4">
                      No commission configuration found.
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>

          {/* Mobile Card View */}
          <div className="d-block d-md-none p-3">
            {rows.length === 0 ? (
              <div className="text-center text-muted py-4">
                No commission configuration found.
              </div>
            ) : (
              rows.map((r, idx) => (
                <MobileCommissionCard 
                  key={`${r.course_id}-${r.city_id || "na"}-${idx}`} 
                  row={r} 
                />
              ))
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default AgentCommissions;